import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gamepad2, Package, ScrollText, Settings as SettingsIcon } from 'lucide-react';

import { env } from '@/env';
import { useGameSelection } from '@/hooks/useGameSelection';
import { useHaptics } from '@/hooks/useHaptics';
import { GamePicker } from '@/components/games/GamePicker';
import { SettingsList } from '@/components/settings/SettingsList';
import { Sheet } from '@/components/common/Sheet';
import { PackTab } from './PackTab';
import { Inventory } from './Inventory';
import { Missions } from './Missions';
import { cn } from '@/lib/cn';

type Tasca = 'settings' | 'inventory' | 'missions';

/** L'ordine in cui le pastiglie stanno sulla riga — dà il verso all'animazione. */
const ORDINE: readonly Tasca[] = ['inventory', 'missions', 'settings'];

/**
 * Lo Zaino — tutto quello che porti con te, in un posto solo.
 *
 * Prima il Profilo aveva tre comandi sparsi: due riquadri affiancati (gioco e
 * impostazioni) e un tasto Inventario in fondo. Tre modi di dire «apri una
 * cosa», tre forme diverse, e la schermata che si riempiva a ogni aggiunta.
 *
 * Ora c'è **un oggetto**. Il Profilo torna a essere la tua identità — banner,
 * uovo, nome — e tutto ciò che si «porta» sta dentro una cosa che si apre.
 *
 * Il cambio gioco è finito dentro Impostazioni e non è una retrocessione: è una
 * preferenza, e sta con le altre preferenze. Era stato promosso quando il
 * Profilo era l'unico posto dove metterlo.
 */
export function Backpack({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const { activeGame } = useGameSelection();
  // L'INVENTARIO e' la tasca che si apre per prima: aprendo uno zaino si guarda
  // cosa c'e' dentro, non come e' configurato. Le impostazioni sono l'ultima
  // scelta perche' sono quella che si cerca di proposito.
  const [tasca, setTasca] = useState<Tasca>('inventory');
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * Da quale lato entra la tasca nuova.
   *
   * Le tre pastiglie stanno su una riga, quindi hanno un ORDINE nello spazio:
   * andando verso destra il contenuto entra da destra. È lo stesso vocabolario
   * con cui l'app cambia sezione (`screen-in-left`/`-right` in AppShell), e
   * usarne uno diverso qui direbbe che sta succedendo un'altra cosa.
   */
  const [verso, setVerso] = useState<'screen-in-left' | 'screen-in-right'>('screen-in-right');

  const scegli = (t: Tasca) => {
    if (t === tasca) return;
    haptics.selection();
    setVerso(ORDINE.indexOf(t) < ORDINE.indexOf(tasca) ? 'screen-in-left' : 'screen-in-right');
    setTasca(t);
  };

  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        label={t('pack.title', { defaultValue: 'Backpack' })}
        bounce
        // `pb` alto: l'apertura supera il punto di riposo di un paio di punti, e
        // senza questo fondo si vedrebbe lo schermo sotto per due decimi di
        // secondo. Vedi il keyframe `pack-up` in globals.css.
        // La CUCITURA anche qui: ce l'hanno la cinghia, la linguetta e la targa
        // in alto, e il pannello era l'unico pezzo di cuoio senza. Anello
        // interno e non `border`, come su tutti gli altri: segue gli angoli
        // stondati e non aggiunge un pixel all'ingombro.
        className="pack-scope h-[86vh] max-h-[86vh] bg-pack pb-[calc(max(1rem,env(safe-area-inset-bottom))+6vh)] [box-shadow:inset_0_0_0_1px_var(--color-pack-stitch)]"
        // La linguetta sporge SOPRA il pannello, non ci sta dentro: è la stessa
        // cinghia di prima, nello stesso posto, che ha solo cambiato scritta e
        // verso della freccia. Dentro il pannello sarebbe scorsa via col
        // contenuto — e una cinghia che scorre via non è più una cinghia.
        above={
          <PackTab
            open
            onToggle={onClose}
            label={t('pack.close', { defaultValue: 'Close backpack' })}
            // Qui il fondo alto non serve: sopra il pannello non c'è nessun
            // bottone da scavalcare.
            className="pb-1.5"
          />
        }
        // Niente maniglia dentro: la linguetta sopra è già la cosa da afferrare,
        // e due maniglie direbbero che ce ne sono due.
        handle={<div className="h-3" />}
      >
        <div className="flex flex-col gap-4 px-4 pt-1">
          {/* Le tre tasche. La rotella è TONDA e le altre due sono pastiglie:
              non è capriccio — le impostazioni sono l'unica voce che si
              riconosce dall'icona da sola, quindi non ha bisogno della parola e
              può occupare meno spazio. */}
          <div className="flex items-center gap-2">
            <TascaPillola
              attiva={tasca === 'inventory'}
              onClick={() => scegli('inventory')}
              icon={Package}
              label={t('inventory', { defaultValue: 'Inventory' })}
            />
            <TascaPillola
              attiva={tasca === 'missions'}
              onClick={() => scegli('missions')}
              icon={ScrollText}
              label={t('missions', { defaultValue: 'Missions' })}
            />
            <TascaTonda
              attiva={tasca === 'settings'}
              onClick={() => scegli('settings')}
              label={t('settings', { defaultValue: 'Settings' })}
            />
          </div>

          {/* Il contenuto della tasca, che ENTRA dal lato giusto.
              `key={tasca}`: rimontando, l'animazione riparte da capo — senza,
              React riuserebbe lo stesso nodo e il fotogramma iniziale non
              esisterebbe. Costa il ripristino dello stato interno della tasca,
              che è il comportamento giusto: tornando all'Inventario si rivuole
              la griglia, non la scheda dell'oggetto lasciata aperta. */}
          <div key={tasca} className={verso}>
            {tasca === 'settings' && (
              <div className="flex flex-col gap-6 pb-2">
                {/* Il cambio gioco in cima all'elenco: è una preferenza come le
                  altre, ma è quella che si tocca più spesso. */}
                <button
                  type="button"
                  onClick={
                    env.featureCrOnly
                      ? undefined
                      : () => {
                          haptics.impact('light');
                          setPickerOpen(true);
                        }
                  }
                  className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-brand">
                    <Gamepad2 className="h-4 w-4" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-semibold text-fg">
                      {t('game', { defaultValue: 'Game' })}
                    </span>
                    <span className="truncate text-xs text-fg-muted">
                      {env.featureCrOnly
                        ? 'Clash Royale'
                        : (activeGame?.name ?? t('game_pick', { defaultValue: 'Choose a game' }))}
                    </span>
                  </span>
                </button>

                <SettingsList />
              </div>
            )}

            {tasca === 'inventory' && <Inventory />}

            {tasca === 'missions' && <Missions />}
          </div>
        </div>
      </Sheet>

      <GamePicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
    </>
  );
}

/** Le impostazioni: tonda, solo icona. */
function TascaTonda({
  attiva,
  onClick,
  label,
}: {
  attiva: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={attiva}
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-opacity active:opacity-80',
        // L'attiva si accende in cuoio chiaro, non in accento: dentro lo zaino
        // l'accento non ha nulla da dire — non è «dove sei» nell'app, è quale
        // tasca stai guardando (ADR-0038, punto 12).
        attiva ? 'bg-pack-fg text-pack-deep' : 'bg-pack-deep text-pack-muted',
      )}
    >
      <SettingsIcon className="h-5 w-5" strokeWidth={2.5} />
    </button>
  );
}

/** Inventario e Missioni: pastiglia con icona e parola. */
function TascaPillola({
  attiva,
  onClick,
  icon: Icon,
  label,
}: {
  attiva: boolean;
  onClick: () => void;
  icon: typeof Package;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={attiva}
      className={cn(
        // `h-11` come la tonda, non un padding a occhio: sono tre bottoni sulla
        // stessa riga, e bastava un pixel di differenza per farli leggere come
        // due file. L'altezza la decide la stessa misura per tutti e tre.
        'flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full px-3',
        'text-xs font-black uppercase tracking-wide transition-opacity active:opacity-80',
        attiva ? 'bg-pack-fg text-pack-deep' : 'bg-pack-deep text-pack-muted',
      )}
    >
      <Icon aria-hidden className="h-4 w-4 shrink-0" strokeWidth={2.5} />
      <span className="truncate">{label}</span>
    </button>
  );
}
